import { api } from "jiffy-api";
import { track } from "jiffy-analytics";

const BANNER_SELECTOR = ".js-pdp-banner";
const MAX_CACHE_SIZE = 50;

const SELECTORS = {
  CTA_BUTTON: ".js-pdp-banner-cta-button",
  CTA_LINK: ".js-pdp-banner-cta-link",
  DELIVERY_DATE: ".js-pdp-banner-delivery-date"
};

export const deliveryDateCache = new Map();
const bannerInstances = new WeakMap();

/**
 * @param {string} productSlug
 * @param {string} compositionName
 * @returns {Promise<string|null>}
 */
const cacheKey = (productSlug, compositionName) => `${productSlug}::${compositionName || ""}`;

const fetchDeliveryDate = async (productSlug, compositionName) => {
  const key = cacheKey(productSlug, compositionName);
  if (deliveryDateCache.has(key)) return deliveryDateCache.get(key);

  try {
    const response = await api.product.fetchDeliveryEstimatesForCurrentCart(
      { productId: productSlug },
      { composition_name: compositionName, variants_hash: JSON.stringify({}) }
    );

    const date = response?.composition_dates?.value_full;
    if (!date) return null;

    if (deliveryDateCache.size >= MAX_CACHE_SIZE) {
      const oldestKey = deliveryDateCache.keys().next().value;
      deliveryDateCache.delete(oldestKey);
    }
    deliveryDateCache.set(key, date);
    return date;
  } catch (error) {
    console.warn("Failed to fetch delivery date:", error);
    return null;
  }
};

export class PromotionBannerClass {
  /**
   * @param {Object} props
   * @param {HTMLElement} props.container
   * @param {Function|null} [props.onButtonClick]
   * @param {Function|null} [props.onLinkClick]
   */
  constructor({ container = null, onButtonClick = null, onLinkClick = null } = {}) {
    this.$container = container;
    this.onButtonClick = onButtonClick;
    this.onLinkClick = onLinkClick;
  }

  get $button() {
    return this.$container?.querySelector(SELECTORS.CTA_BUTTON);
  }

  get $link() {
    return this.$container?.querySelector(SELECTORS.CTA_LINK);
  }

  get $deliveryDates() {
    return [...(this.$container?.querySelectorAll(SELECTORS.DELIVERY_DATE) || [])];
  }

  /** @returns {Promise<void>} */
  async initDeliveryDate() {
    const $elements = this.$deliveryDates;
    if (!$elements.length) return;

    const { deliveryProductSlug, deliveryCompositionName } = this.$container.dataset;
    if (!deliveryProductSlug) return;

    const date = await fetchDeliveryDate(deliveryProductSlug, deliveryCompositionName);
    if (date) {
      $elements.forEach($element => {
        $element.textContent = date;
      });
    }
  }

  /** @returns {void} */
  initViewTracking() {
    const location = this.$container.dataset.bannerLocation || "";

    this.viewObserver = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry || !entry.isIntersecting || !this.viewObserver) return;

        track("promo_view", {
          is_peekaboo_tile: entry.intersectionRatio < 0.5,
          location
        });

        this.viewObserver.disconnect();
        this.viewObserver = null;
      },
      { threshold: [0, 0.5] }
    );

    this.viewObserver.observe(this.$container);
  }

  /**
   * @param {Element} $el
   * @returns {void}
   */
  static trackClick($el) {
    track("promo_click", {
      cta_text: $el.textContent.trim(),
      destination: $el.href || ""
    });
  }

  /** @returns {boolean} */
  init() {
    if (!this.$container) return false;

    this.handleButtonClick = event => {
      PromotionBannerClass.trackClick(event.currentTarget);
      this.onButtonClick?.(event);
    };

    this.handleLinkClick = event => {
      PromotionBannerClass.trackClick(event.currentTarget);
      this.onLinkClick?.(event);
    };

    if (this.$button) {
      this.$button.addEventListener("click", this.handleButtonClick);
    }

    if (this.$link) {
      this.$link.addEventListener("click", this.handleLinkClick);
    }

    this.initViewTracking();
    this.initDeliveryDate();

    return true;
  }

  /** @returns {void} */
  destroy() {
    if (this.$button && this.handleButtonClick) {
      this.$button.removeEventListener("click", this.handleButtonClick);
      this.handleButtonClick = null;
    }

    if (this.$link && this.handleLinkClick) {
      this.$link.removeEventListener("click", this.handleLinkClick);
      this.handleLinkClick = null;
    }

    if (this.viewObserver) {
      this.viewObserver.disconnect();
      this.viewObserver = null;
    }
  }
}

export const initAllBanners = () => {
  const $containers = document.querySelectorAll(BANNER_SELECTOR);
  $containers.forEach($container => {
    const existingBanner = bannerInstances.get($container);
    if (existingBanner) {
      existingBanner.initDeliveryDate();
      return;
    }

    const banner = new PromotionBannerClass({ container: $container });
    if (banner.init()) {
      bannerInstances.set($container, banner);
    }
  });
};
